import { useEffect, useState, type FormEvent } from 'react';
import { CREDIT_TEXT_LIMITS, type PanelCreditGrantDto } from '@icebreaker/shared';
import { Button, Field, Input, Modal, Textarea, useFormErrors } from '@icebreaker/ui';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { creditMutationReset, reverseCredits } from '../store/credits.slice';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  failureKind,
  fieldErrorPaths,
  isConflict,
  useReleaseKeyOnSettle,
  useWriteKey,
  useStaffName,
} from './dialog-parts';
import styles from './organizations.module.css';

/**
 * TERS KAYDET (P6 · 2). Yalnız KULLANILMAMIŞ kısım ters kaydedilebilir
 * (`reversible`, sunucu hesaplar). İlk kayıt silinmez, defterde ters kayıt
 * satırı oluşur. Birincil eylem yıkıcı görünümdedir (hata rengi yalnız burada).
 */
export function ReverseCreditsDialog({
  organizationId,
  grant,
  onClose,
}: {
  organizationId: string;
  grant: PanelCreditGrantDto;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const staffName = useStaffName();
  const mutation = useAppSelector((root: RootState) => root.panelCredits.mutations.reverse);
  const [amountText, setAmountText] = useState(String(grant.reversible));
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const amount = Number.parseInt(amountText, 10);
  const signature = JSON.stringify({ amount, reason: reason.trim() });
  // Kapsam kurum + hak: aynı hakka aynı gövde, kesin yanıta kadar aynı anahtarla gider.
  const writeKey = useWriteKey(`reverse:${organizationId}:${grant.id}`);
  const outcome = failureKind(mutation.phase, mutation.error);

  useEffect(() => {
    dispatch(creditMutationReset('reverse'));
    return () => {
      dispatch(creditMutationReset('reverse'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (mutation.phase === 'succeeded') onClose();
  }, [mutation.phase, onClose]);
  useReleaseKeyOnSettle(mutation.phase, outcome, writeKey.release);

  const busy = mutation.phase === 'loading';
  const serverPaths = fieldErrorPaths(mutation.error);
  const serverForm = useFormErrors(mutation.error, ['amount', 'reason']);
  const serverLimit = mutation.error?.fieldErrors?.find((item) => item.path === 'amount')?.params
    ?.limit;
  const max = typeof serverLimit === 'number' ? serverLimit : grant.reversible;
  const amountValid = Number.isInteger(amount) && amount >= 1 && amount <= max;

  let amountError: string | undefined;
  if (touched || amountText !== String(grant.reversible) || serverPaths.has('amount')) {
    if (!Number.isInteger(amount) || amount < 1) {
      amountError = t.t('admin.credit.reverseDialog.amountMin');
    } else if (amount > max || serverPaths.has('amount')) {
      amountError = t.t('admin.credit.reverseDialog.amountMax', { count: max });
    }
  }
  const reasonError =
    touched && reason.trim() === ''
      ? t.t('admin.credit.reverseDialog.reasonRequired')
      : serverPaths.has('reason')
        ? (serverForm.at('reason') ?? t.t('admin.credit.reverseDialog.reasonRequired'))
        : undefined;
  const conflict = isConflict(mutation.error);
  const failed = outcome !== 'none';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTouched(true);
    if (!amountValid || reason.trim() === '') return;
    dispatch(
      reverseCredits.request({
        organizationId,
        grantId: grant.id,
        body: { amount, reason: reason.trim(), idempotencyKey: writeKey.keyFor(signature) },
      }),
    );
  };

  const granter = grant.grantedBy.displayName ?? t.t('admin.credit.ledger.system');
  const submitLabel = amountValid
    ? t.t('admin.credit.reverseDialog.submit', { count: amount })
    : t.t('admin.credit.reverseDialog.submitPlain');

  return (
    <Modal
      open
      title={t.t('admin.credit.reverseDialog.title')}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="reverse-credits-form"
            loading={busy}
            disabled={!busy && !amountValid}
            loadingLabel={t.t('admin.credit.reverseDialog.submitting')}
          >
            {failed ? t.t('admin.orgs.retry') : submitLabel}
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub>
        {t.t('admin.credit.reverseDialog.sub', {
          date: t.formatDate(grant.grantedAt, numericDateOptions(t.locale)),
          kind: t.t(`admin.credit.kinds.${grant.kind}`),
          name: granter,
        })}
      </DialogSub>
      <form id="reverse-credits-form" className={styles.form} onSubmit={submit} noValidate>
        <Field
          id="reverse-amount"
          label={t.t('admin.credit.reverseDialog.amount')}
          hint={
            amountError
              ? undefined
              : // Ters kaydedilen harcama SAYILMAZ (T4 QA P3-1): ikisi ayrı yazılır.
                grant.reversed > 0
                ? t.t('admin.credit.reverseDialog.amountHintReversed', {
                    total: t.formatNumber(grant.amount),
                    used: t.formatNumber(grant.consumed),
                    reversed: t.formatNumber(grant.reversed),
                    count: grant.reversible,
                  })
                : t.t('admin.credit.reverseDialog.amountHint', {
                    total: t.formatNumber(grant.amount),
                    used: t.formatNumber(grant.consumed),
                    count: grant.reversible,
                  })
          }
          error={amountError}
        >
          <Input
            id="reverse-amount"
            type="number"
            inputMode="numeric"
            min={1}
            max={grant.reversible}
            value={amountText}
            disabled={busy}
            invalid={Boolean(amountError)}
            onChange={(event) => setAmountText(event.target.value)}
          />
        </Field>
        <Field
          id="reverse-reason"
          label={t.t('admin.credit.reverseDialog.reason')}
          error={reasonError}
        >
          <Textarea
            id="reverse-reason"
            rows={2}
            value={reason}
            maxLength={CREDIT_TEXT_LIMITS.reason}
            disabled={busy}
            invalid={Boolean(reasonError)}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <InfoBlock tone="warn">{t.t('admin.credit.reverseDialog.warn')}</InfoBlock>
        {conflict ? (
          <ServerBanner>{t.t('admin.credit.conflict')}</ServerBanner>
        ) : outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedLedger')} />
        ) : outcome === 'rejected' ? (
          <ServerBanner>{t.t('admin.credit.reverseDialog.serverError')}</ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}
